import { ZfLoadingSpinner } from '@zafire/react';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { useCallback, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation } from 'react-router';
import { useWealthPlanningNavigate } from '../../hooks/useWealthPlanningNavigate';
import { serializeSignInState } from '../../utils/authUtils';
import { useAdfsExpiredRefreshTokenHandling } from './useAdfsExpiredRefreshTokenHandling';

interface IAuthenticatedProps extends PropsWithChildren {
    silent?: boolean;
}

export const Authenticated: FunctionComponent<IAuthenticatedProps> = ({ children, silent = false }) => {
    const auth = useAuth();
    useAdfsExpiredRefreshTokenHandling(auth);
    const location = useLocation();
    const { navigate } = useWealthPlanningNavigate();

    const signin = useCallback(() => {
        const state = serializeSignInState({ returnTo: `${location.pathname}${location.search}` });

        if (silent) {
            auth.signinSilent();
        } else {
            auth.signinRedirect({ state });
        }
    }, [auth, location.search, location.pathname, silent]);

    useEffect(() => {
        if (!auth.isLoading && !auth.activeNavigator && !auth.error && !auth.isAuthenticated) {
            signin();
        }
    }, [auth, signin]);

    switch (auth.activeNavigator) {
        case 'signinSilent':
            return <ZfLoadingSpinner style={{ margin: 'auto' }} />;
        case 'signoutRedirect':
            return <ZfLoadingSpinner style={{ margin: 'auto' }} />;
    }

    if (auth.isLoading) return <ZfLoadingSpinner style={{ margin: 'auto' }} />;

    if (auth.error) {
        console.error(auth.error);
        navigate('/error');
    }

    if (!auth.isAuthenticated) return <ZfLoadingSpinner style={{ margin: 'auto' }} />;

    return <>{children}</>;
};
